import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, ArrowsClockwise, CheckCircle, HandCoins, FloppyDisk, Trash, PencilSimple } from "@phosphor-icons/react";

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Payroll() {
    const { user } = useAuth();
    const canApprove = user?.role === "founder" || user?.role === "hr";
    const canPost = user?.role === "founder" || user?.role === "accountant";
    const [month, setMonth] = useState(currentMonth());
    const [runs, setRuns] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draftOpen, setDraftOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [approveTarget, setApproveTarget] = useState(null);
    const [payTarget, setPayTarget] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [r, e, lg] = await Promise.all([
                api.get("/payroll/runs", { params: { month } }),
                api.get("/employees"),
                api.get("/ledgers"),
            ]);
            setRuns(r.data);
            setEmployees(e.data);
            setLedgers(lg.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [month]);
    useEffect(() => { refresh(); }, [refresh]);

    const bankCash = useMemo(() => ledgers.filter((l) => ["bank", "cash"].includes(l.party_type)), [ledgers]);
    const totals = useMemo(() => {
        const gross = runs.reduce((s, r) => s + (r.gross_earned || 0), 0);
        const net = runs.reduce((s, r) => s + (r.net_payable || 0), 0);
        const paid = runs.reduce((s, r) => s + (r.amount_paid || 0), 0);
        const outstanding = runs.reduce((s, r) => s + (r.outstanding || 0), 0);
        return { gross, net, paid, outstanding };
    }, [runs]);
    const employeesWithoutRun = employees.filter(
        (e) => e.active && e.mode === "payroll" && !runs.find((r) => r.employee_id === e.id),
    );

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Payroll runs"
                sub="Draft → founder approves → auto-post expense journal. Record payments (Stage 4 will auto-match from bank statement)."
                right={
                    <div className="flex items-center gap-2">
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="pr-month" />
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="pr-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button onClick={() => setDraftOpen(true)} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pr-new">
                            <Plus size={14} className="mr-2" /> New payroll draft
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                <div className="grid grid-cols-4 gap-4">
                    <Stat label="Gross earned" value={totals.gross} />
                    <Stat label="Net payable" value={totals.net} tone="teal" />
                    <Stat label="Paid" value={totals.paid} />
                    <Stat label="Outstanding" value={totals.outstanding} tone="amber" bold />
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        {month} — {runs.length} run{runs.length === 1 ? "" : "s"}
                    </div>
                    <table className="se-table" data-testid="pr-table">
                        <thead>
                            <tr>
                                <th>Employee</th><th>Tier</th><th className="text-right">Base</th>
                                <th className="text-right">OT hrs / amt</th><th className="text-right">Gross</th>
                                <th className="text-right">Deductions</th><th className="text-right">Net</th>
                                <th className="text-right">Paid / Owed</th><th>Status</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 ? (
                                <tr><td colSpan="10" className="py-10 text-center text-stone-500">
                                    No payroll runs for {month}. Click &quot;New payroll draft&quot; to start.
                                </td></tr>
                            ) : runs.map((r) => (
                                <tr key={r.id} data-testid={`pr-row-${r.id}`}>
                                    <td>{r.employee_name} <span className="text-[10px] text-stone-500">({r.employee_code})</span></td>
                                    <td className="text-xs capitalize">{r.tier}</td>
                                    <td className="se-num text-right">₹ {inr(r.prorated_base)}
                                        {r.prorate_ratio < 1 ? <div className="text-[10px] text-stone-500">×{r.prorate_ratio}</div> : null}</td>
                                    <td className="se-num text-right">{r.ot_hours || 0} · ₹ {inr(r.ot_amount)}</td>
                                    <td className="se-num text-right font-medium">₹ {inr(r.gross_earned)}
                                        {r.commission > 0 ? <div className="text-[10px] text-emerald-700">+comm ₹{inr(r.commission)}</div> : null}</td>
                                    <td className="se-num text-right text-xs">
                                        {r.tds_deduction ? <div>TDS ₹{inr(r.tds_deduction)}</div> : null}
                                        {r.pf_employee_deduction ? <div>PF ₹{inr(r.pf_employee_deduction)}</div> : null}
                                        {r.pt_deduction ? <div>PT ₹{inr(r.pt_deduction)}</div> : null}
                                        {r.advance_recovery ? <div className="text-amber-700">EMI ₹{inr(r.advance_recovery)}</div> : null}
                                    </td>
                                    <td className="se-num text-right font-medium" style={{ color: "#0f766e" }}>₹ {inr(r.net_payable)}</td>
                                    <td className="se-num text-right text-xs">
                                        {r.status === "posted" || r.status === "fully_paid" ? (
                                            <>
                                                <div>₹ {inr(r.amount_paid || 0)}</div>
                                                {r.outstanding > 0 ? <div className="text-amber-700">owe ₹ {inr(r.outstanding)}</div> : null}
                                            </>
                                        ) : "—"}
                                    </td>
                                    <td><StatusChip status={r.status} /></td>
                                    <td className="text-right">
                                        {r.status === "draft" || r.status === "submitted" ? (
                                            <>
                                                <Button size="sm" variant="outline"
                                                        onClick={() => setEditTarget(r)}
                                                        className="mr-1"
                                                        data-testid={`pr-edit-${r.id}`}
                                                        title="Edit draft">
                                                    <PencilSimple size={13} />
                                                </Button>
                                                <Button size="sm" variant="outline"
                                                        onClick={async () => {
                                                            if (!window.confirm(`Delete draft payroll for ${r.employee_name}?`)) return;
                                                            try {
                                                                await api.delete(`/payroll/runs/${r.id}`);
                                                                toast.success("Draft deleted");
                                                                refresh();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }}
                                                        className="mr-1"
                                                        style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                                        data-testid={`pr-delete-${r.id}`}
                                                        title="Delete draft">
                                                    <Trash size={13} />
                                                </Button>
                                                {canApprove ? (
                                                    <Button size="sm" onClick={() => setApproveTarget(r)}
                                                        style={{ background: "#16a34a", color: "#fff" }}
                                                        data-testid={`pr-approve-${r.id}`}>
                                                        <CheckCircle size={13} className="mr-1" /> Approve
                                                    </Button>
                                                ) : (
                                                    <span className="text-[10px] text-stone-500 italic" data-testid={`pr-pending-${r.id}`}>Awaiting HR/Founder</span>
                                                )}
                                            </>
                                        ) : r.status === "approved" ? (
                                            canPost ? (
                                                <Button size="sm" onClick={async () => {
                                                    try { await api.post(`/payroll/runs/${r.id}/post`); toast.success("Posted to books"); refresh(); }
                                                    catch (e) { toast.error(formatApiError(e)); }
                                                }} style={{ background: "#0d6efd", color: "#fff" }} data-testid={`pr-post-${r.id}`}>
                                                    Post
                                                </Button>
                                            ) : (
                                                <span className="text-[10px] text-emerald-700 italic" data-testid={`pr-approved-${r.id}`}>
                                                    Approved — waiting for accountant to post
                                                </span>
                                            )
                                        ) : r.status === "posted" && r.outstanding > 0 ? (
                                            canPost ? (
                                                <Button size="sm" onClick={() => setPayTarget(r)}
                                                        style={{ background: "#f59e0b", color: "#fff" }} data-testid={`pr-pay-${r.id}`}
                                                        title="Book payment from Cash or Fiat. For real bank transfers, upload the bank statement instead.">
                                                    <HandCoins size={13} className="mr-1" /> Pay as Cash/Fiat
                                                </Button>
                                            ) : (
                                                <span className="text-[10px] text-amber-700 italic" data-testid={`pr-awaiting-pay-${r.id}`}>
                                                    Posted — awaiting accountant payout
                                                </span>
                                            )
                                        ) : null}
                                        {(r.status === "posted" || r.status === "fully_paid") ? (
                                            <Button
                                                size="sm" variant="outline"
                                                onClick={async () => {
                                                    try {
                                                        const res = await api.get(`/payroll/runs/${r.id}/payslip`,
                                                            { responseType: "blob" });
                                                        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                                        const a = document.createElement("a");
                                                        a.href = url;
                                                        a.download = `payslip-${r.employee_code || r.employee_id}-${r.month}.pdf`;
                                                        document.body.appendChild(a); a.click();
                                                        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                                                    } catch (e) { toast.error(formatApiError(e)); }
                                                }}
                                                className="ml-1"
                                                data-testid={`pr-payslip-${r.id}`}
                                                title="Download PDF payslip"
                                            >
                                                Payslip
                                            </Button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {employeesWithoutRun.length > 0 ? (
                    <div className="text-[11px] text-stone-500">
                        {employeesWithoutRun.length} active payroll employee{employeesWithoutRun.length === 1 ? "" : "s"} without a run this month: {employeesWithoutRun.map((e) => e.name).join(", ")}
                    </div>
                ) : null}
            </div>

            <DraftDialog open={draftOpen} onOpenChange={setDraftOpen} employees={employeesWithoutRun} month={month} onSaved={refresh} />
            <EditDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={refresh} />
            <ApproveDialog target={approveTarget} onClose={() => setApproveTarget(null)} onSaved={refresh} />
            <PayDialog target={payTarget} onClose={() => setPayTarget(null)} bankCash={bankCash} onSaved={refresh} />
        </>
    );
}

function DraftDialog({ open, onOpenChange, employees, month, onSaved }) {
    const [empId, setEmpId] = useState("");
    const [ot, setOt] = useState("0");
    const [incentive, setIncentive] = useState("0");
    const [penalty, setPenalty] = useState("0");
    const [revenue, setRevenue] = useState("0");
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snapshotOt, setSnapshotOt] = useState(null);

    useEffect(() => {
        if (open) {
            setEmpId(""); setOt("0"); setIncentive("0"); setPenalty("0");
            setRevenue("0"); setPreview(null); setSnapshotOt(null);
        }
    }, [open]);

    // When the founder picks an employee, prefill OT from that employee's
    // biometric snapshot (if any) so the popup reflects the real attendance.
    // Non-labour or missing snapshots fall back to 0.
    useEffect(() => {
        let cancelled = false;
        if (!empId) { setSnapshotOt(null); return; }
        (async () => {
            try {
                const { data } = await api.get("/biometric/snapshots", { params: { month } });
                const snap = (data || []).find((s) => s.employee_id === empId);
                if (cancelled) return;
                const snapOt = snap ? Number(snap.ot_hours || 0) : 0;
                setSnapshotOt(snap ? snapOt : null);
                setOt(String(snapOt));
            } catch {
                if (!cancelled) setSnapshotOt(null);
            }
        })();
        return () => { cancelled = true; };
    }, [empId, month]);

    const runPreview = async () => {
        if (!empId) return;
        try {
            const { data } = await api.post("/payroll/preview", {
                employee_id: empId, month,
                ot_hours: Number(ot), incentive: Number(incentive),
                penalty: Number(penalty), revenue_closed: Number(revenue),
            });
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { if (empId) runPreview(); }, [empId, ot, incentive, penalty, revenue]);

    const submit = async () => {
        if (!empId) return toast.error("Pick an employee");
        setSaving(true);
        try {
            await api.post("/payroll/runs", {
                employee_id: empId, month,
                ot_hours: Number(ot), incentive: Number(incentive),
                penalty: Number(penalty), revenue_closed: Number(revenue),
            });
            toast.success("Draft saved. Founder can now approve.");
            onOpenChange(false); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const emp = employees.find((e) => e.id === empId);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="pr-draft-dialog">
                <DialogHeader><DialogTitle>New payroll draft — {month}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div><Label>Employee *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={empId}
                                onChange={(e) => setEmpId(e.target.value)} data-testid="pr-draft-emp">
                            <option value="">— Select —</option>
                            {employees.map((e) => (
                                <option key={e.id} value={e.id}>{e.name} ({e.employee_code}) · {e.tier} · base ₹{inr(e.base_salary)}</option>
                            ))}
                        </select></div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>OT hours</Label>
                            <Input type="number" value={ot} onChange={(e) => setOt(e.target.value)} data-testid="pr-ot" />
                            {snapshotOt !== null ? (
                                <div className="text-[10px] text-stone-500 mt-1" data-testid="pr-ot-snapshot-hint">
                                    Biometric snapshot: {snapshotOt} hrs · you can override live
                                </div>
                            ) : null}
                        </div>
                        <div><Label>Incentive (₹)</Label>
                            <Input type="number" value={incentive} onChange={(e) => setIncentive(e.target.value)} data-testid="pr-inc" /></div>
                        <div><Label>Penalty (₹)</Label>
                            <Input type="number" value={penalty} onChange={(e) => setPenalty(e.target.value)} data-testid="pr-pen" /></div>
                    </div>
                    {emp?.commission_eligible ? (
                        <div><Label>Sales revenue closed this month (₹)</Label>
                            <Input type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="pr-rev" />
                            <div className="text-[10px] text-stone-500 mt-1">
                                Threshold: base × {emp.commission_threshold_multiplier} = ₹{inr((emp.base_salary || 0) * emp.commission_threshold_multiplier)}
                            </div>
                        </div>
                    ) : null}
                    {preview ? (
                        <div className="border rounded-sm p-3 text-xs space-y-1" style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}>
                            <div className="font-medium text-sm mb-2">Preview</div>
                            <div>Base (prorated ×{preview.prorate_ratio}): ₹{inr(preview.prorated_base)}</div>
                            <div>OT rate ₹{preview.ot_rate}/hr × {preview.ot_hours} hrs = ₹{inr(preview.ot_amount)}</div>
                            <div>Incentive: +₹{inr(preview.incentive)} · Penalty: −₹{inr(preview.penalty)}</div>
                            {preview.commission > 0 ? <div>Commission: +₹{inr(preview.commission)}</div> : null}
                            <div className="font-medium">Gross: ₹{inr(preview.gross_earned)}</div>
                            <div>TDS: −₹{inr(preview.tds_deduction)} · PF: −₹{inr(preview.pf_employee_deduction)}
                                {preview.pt_deduction > 0 ? <> · PT: −₹{inr(preview.pt_deduction)}</> : null}
                                {preview.advance_recovery > 0 ? <> · EMI recovery: −₹{inr(preview.advance_recovery)}</> : null}</div>
                            <div className="font-medium text-emerald-700" data-testid="pr-preview-net">Net payable: ₹{inr(preview.net_payable)}</div>
                        </div>
                    ) : null}
                    {preview && preview.pending_disbursement_advances && preview.pending_disbursement_advances.length > 0 ? (
                        <div className="border rounded-sm p-3 text-xs"
                             style={{ borderColor: "#fde68a", background: "#fffbeb", color: "#92400e" }}
                             data-testid="pr-advance-pending-warning">
                            <div className="font-medium mb-1">⚠ Approved advance not yet disbursed</div>
                            {preview.pending_disbursement_advances.map((a) => (
                                <div key={a.id}>
                                    · ₹{inr(a.amount)} — {a.emi_count} × ₹{inr(a.emi_amount)} from {a.first_emi_month} ({a.reason})
                                    {a.status === "pending_bank_disbursement" ? (
                                        <span className="text-[10px] ml-1 opacity-80">
                                            — queued for {a.intended_source_ledger_name}{a.ref_no ? ` · ${a.ref_no}` : ""}, awaiting bank statement
                                        </span>
                                    ) : null}
                                </div>
                            ))}
                            <div className="mt-2 opacity-90">
                                {preview.pending_disbursement_advances.some((a) => a.status === "pending_bank_disbursement")
                                    ? <>These advances are queued against real bank accounts. EMI recovery starts when the accountant uploads that bank&apos;s statement and matches the debit line.</>
                                    : <>Payroll only recovers EMI after the money leaves the company. Head to <b>Advances (staff)</b> and click <b>Disburse</b> to release the funds — the next payroll will then auto-deduct the EMI.</>}
                            </div>
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !empId} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pr-draft-submit">
                        <FloppyDisk size={14} className="mr-2" /> {saving ? "…" : "Save draft"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ApproveDialog({ target, onClose, onSaved }) {
    const [inc, setInc] = useState("0");
    const [pen, setPen] = useState("0");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (target) { setInc("0"); setPen("0"); } }, [target]);
    if (!target) return null;
    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/payroll/runs/${target.id}/approve`, {
                founder_incentive: Number(inc), founder_penalty: Number(pen),
            });
            toast.success("Approved");
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="pr-approve-dialog">
                <DialogHeader><DialogTitle>Approve payroll — {target.employee_name}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-stone-600">Current net: <b>₹ {inr(target.net_payable)}</b></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Founder incentive (₹)</Label>
                            <Input type="number" value={inc} onChange={(e) => setInc(e.target.value)} data-testid="pr-founder-inc" /></div>
                        <div><Label>Founder penalty (₹)</Label>
                            <Input type="number" value={pen} onChange={(e) => setPen(e.target.value)} data-testid="pr-founder-pen" /></div>
                    </div>
                    <div className="text-[11px] text-stone-500">
                        These add on top of the manager&apos;s numbers. Both are preserved separately for audit trail.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#16a34a", color: "#fff" }} data-testid="pr-approve-submit">
                        {saving ? "…" : "Approve"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PayDialog({ target, onClose, bankCash, onSaved }) {
    const [src, setSrc] = useState("");
    const [d, setD] = useState(new Date().toISOString().slice(0, 10));
    const [amount, setAmount] = useState("");
    const [saving, setSaving] = useState(false);
    // Cash/Fiat only. Real bank payments must flow through the Bank Statement
    // upload → auto-map path so ledger + statement stay in sync.
    const cashFiatOnly = useMemo(() => bankCash.filter((l) => l.system === true), [bankCash]);
    useEffect(() => {
        if (target) {
            setAmount(String(target.outstanding || target.net_payable));
            setSrc(cashFiatOnly.find((l) => l.name === "FIAT BANK")?.id || (cashFiatOnly[0]?.id || ""));
        }
    }, [target, cashFiatOnly]);
    if (!target) return null;
    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/payroll/runs/${target.id}/pay`, {
                source_ledger_id: src, date: d, amount: Number(amount),
            });
            toast.success("Payment recorded");
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="pr-pay-dialog">
                <DialogHeader><DialogTitle>Pay {target.employee_name} — Cash / Fiat</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="text-[11px] rounded-sm px-3 py-2" style={{ background: "#fef3c7", color: "#78350f" }}>
                        Use this only for cash-in-hand or Fiat drawer payouts. For ICICI / SBI transfers,
                        skip this — upload the bank statement and it will auto-map.
                    </div>
                    <div className="text-sm">Outstanding: <b>₹ {inr(target.outstanding || target.net_payable)}</b></div>
                    <div><Label>From</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={src}
                                onChange={(e) => setSrc(e.target.value)} data-testid="pr-pay-src">
                            {cashFiatOnly.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Date</Label>
                            <Input type="date" value={d} onChange={(e) => setD(e.target.value)} data-testid="pr-pay-date" /></div>
                        <div><Label>Amount (₹)</Label>
                            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pr-pay-amount" /></div>
                    </div>
                    <div className="text-[11px] text-stone-500">
                        Partial pay is allowed — outstanding will be tracked until fully paid.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pr-pay-submit">
                        {saving ? "…" : "Record payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditDialog({ target, onClose, onSaved }) {
    const [ot, setOt] = useState("0");
    const [incentive, setIncentive] = useState("0");
    const [penalty, setPenalty] = useState("0");
    const [revenue, setRevenue] = useState("0");
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snapshotOt, setSnapshotOt] = useState(null);

    useEffect(() => {
        if (target) {
            setOt(String(target.ot_hours ?? 0));
            setIncentive(String(target.manager_incentive ?? target.incentive ?? 0));
            setPenalty(String(target.manager_penalty ?? target.penalty ?? 0));
            setRevenue(String(target.revenue_closed ?? 0));
            setPreview(null);
            setSnapshotOt(null);
        }
    }, [target]);

    // Show the current biometric snapshot as a hint (does not overwrite the
    // saved draft's OT — the founder deliberately picked that value).
    useEffect(() => {
        let cancelled = false;
        if (!target) return;
        (async () => {
            try {
                const { data } = await api.get("/biometric/snapshots", { params: { month: target.month } });
                const snap = (data || []).find((s) => s.employee_id === target.employee_id);
                if (!cancelled) setSnapshotOt(snap ? Number(snap.ot_hours || 0) : null);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [target]);

    useEffect(() => {
        let cancelled = false;
        if (!target) return;
        (async () => {
            try {
                const { data } = await api.post("/payroll/preview", {
                    employee_id: target.employee_id, month: target.month,
                    ot_hours: Number(ot), incentive: Number(incentive),
                    penalty: Number(penalty), revenue_closed: Number(revenue),
                });
                if (!cancelled) setPreview(data);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [target, ot, incentive, penalty, revenue]);

    if (!target) return null;

    const submit = async () => {
        setSaving(true);
        try {
            await api.patch(`/payroll/runs/${target.id}`, {
                ot_hours: Number(ot), incentive: Number(incentive),
                penalty: Number(penalty), revenue_closed: Number(revenue),
            });
            toast.success("Draft updated");
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="pr-edit-dialog">
                <DialogHeader>
                    <DialogTitle>Edit payroll draft — {target.employee_name} · {target.month}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>OT hours</Label>
                            <Input type="number" value={ot} onChange={(e) => setOt(e.target.value)} data-testid="pr-edit-ot" />
                            {snapshotOt !== null ? (
                                <div className="text-[10px] text-stone-500 mt-1" data-testid="pr-edit-ot-snapshot-hint">
                                    Biometric snapshot: {snapshotOt} hrs
                                </div>
                            ) : null}
                        </div>
                        <div><Label>Incentive (₹)</Label>
                            <Input type="number" value={incentive} onChange={(e) => setIncentive(e.target.value)} data-testid="pr-edit-inc" /></div>
                        <div><Label>Penalty (₹)</Label>
                            <Input type="number" value={penalty} onChange={(e) => setPenalty(e.target.value)} data-testid="pr-edit-pen" /></div>
                    </div>
                    {(target.revenue_closed ?? 0) > 0 || preview?.commission > 0 ? (
                        <div><Label>Sales revenue closed this month (₹)</Label>
                            <Input type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="pr-edit-rev" /></div>
                    ) : null}
                    {preview ? (
                        <div className="border rounded-sm p-3 text-xs space-y-1" style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}>
                            <div className="font-medium text-sm mb-2">Preview</div>
                            <div>Base (prorated ×{preview.prorate_ratio}): ₹{inr(preview.prorated_base)}</div>
                            <div>OT rate ₹{preview.ot_rate}/hr × {preview.ot_hours} hrs = ₹{inr(preview.ot_amount)}</div>
                            <div>Incentive: +₹{inr(preview.incentive)} · Penalty: −₹{inr(preview.penalty)}</div>
                            {preview.commission > 0 ? <div>Commission: +₹{inr(preview.commission)}</div> : null}
                            <div className="font-medium">Gross: ₹{inr(preview.gross_earned)}</div>
                            <div>TDS: −₹{inr(preview.tds_deduction)} · PF: −₹{inr(preview.pf_employee_deduction)}
                                {preview.pt_deduction > 0 ? <> · PT: −₹{inr(preview.pt_deduction)}</> : null}
                                {preview.advance_recovery > 0 ? <> · EMI recovery: −₹{inr(preview.advance_recovery)}</> : null}</div>
                            <div className="font-medium text-emerald-700" data-testid="pr-edit-preview-net">Net payable: ₹{inr(preview.net_payable)}</div>
                        </div>
                    ) : null}
                    {preview && preview.pending_disbursement_advances && preview.pending_disbursement_advances.length > 0 ? (
                        <div className="border rounded-sm p-3 text-xs"
                             style={{ borderColor: "#fde68a", background: "#fffbeb", color: "#92400e" }}
                             data-testid="pr-edit-advance-pending-warning">
                            <div className="font-medium mb-1">⚠ Approved advance waiting to be disbursed</div>
                            {preview.pending_disbursement_advances.map((a) => (
                                <div key={a.id}>
                                    · ₹{inr(a.amount)} — {a.emi_count} × ₹{inr(a.emi_amount)} from {a.first_emi_month} ({a.reason})
                                </div>
                            ))}
                            <div className="mt-2 opacity-90">
                                EMI recovery starts only after the advance is <b>disbursed</b>. Open <b>Advances (staff)</b> to release the funds — then re-open this draft.
                            </div>
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pr-edit-submit">
                        <FloppyDisk size={14} className="mr-2" /> {saving ? "…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}



function Stat({ label, value, tone, bold }) {
    const color = tone === "amber" ? "#b45309" : tone === "teal" ? "#0f766e" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4" style={{ borderColor: "var(--se-border)" }}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num text-2xl mt-1 ${bold ? "font-medium" : ""}`} style={{ color }}>₹ {inr(value || 0)}</div>
        </div>
    );
}
